import type { ComponentPropsWithoutRef, ReactElement } from 'react';
import React, { useEffect } from 'react';
import { useRouter } from 'next/router';

import clsx from 'clsx';

// Hooks
import useMarkAsReadNotification from '@/utils/hooks/notifications/useMarkAsReadNotification';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import type { SizesType } from '@/types/utils/sizesType';

import type { SocialProfileNetworkType } from '@/types/socialProfileType';
// Types
import type {
    CustomNotificationType,
    NotificationType,
    NotificationVariant
} from '@/utils/hooks/context/toast/types';

import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import Button, { ERROR_SECONDARY, GREEN, PRIMARY } from '@/components/base/Button';
// Components
import Icon from '@/components/base/Icon';
import { Trans } from '@/components/tools/translation';

type VariantsProps = Record<NotificationVariant, string>;

const backgroundVariants: VariantsProps = {
    [PRIMARY]: 'bg-icoBlue-100',
    [GREEN]: 'bg-icoGreen-100',
    [ERROR_SECONDARY]: 'bg-icoRed-100',
    gray_100: 'bg-icoGray-100'
};

const dotVariants: VariantsProps = {
    [PRIMARY]: 'bg-icoBlue',
    [GREEN]: 'bg-icoGreen',
    [ERROR_SECONDARY]: 'bg-icoRed',
    gray_100: 'bg-icoGray-100'
};

type NotificationProps = ComponentPropsWithoutRef<'div'> & {
    dataNotification?: NotificationType | CustomNotificationType;
    type?: keyof typeof backgroundVariants;
    wide?: boolean;
    goToUrl?: string;
    goToExternalUrl?: string;
    onClick?: () => void;
    contentHidden?: boolean;
};

function Notification({
    className,
    goToUrl,
    goToExternalUrl,
    onClick,
    style,
    children,
    type = PRIMARY,
    wide = false,
    contentHidden = false,
    dataNotification
}: NotificationProps) {
    const { t } = useTranslation();
    const background = backgroundVariants[type];
    const backgroundDot = dotVariants[type];
    const isRead = 'read' in dataNotification ? dataNotification.read : false;
    const notificationClickAvailable =
        typeof goToUrl === 'string' ||
        typeof goToExternalUrl === 'string' ||
        typeof onClick === 'function';

    return (
        <div
            data-testid="notification"
            style={style}
            className={clsx(
                'group block w-full rounded-md border-3 border-white bg-white text-left transition-all dark:border-icoDarkMode-deepBlack dark:bg-icoDarkMode-deepBlack',
                className,
                {
                    'hover:shadow-icoXXL': wide
                }
            )}>
            <span
                className={clsx(
                    'relative flex items-center gap-2 overflow-hidden rounded dark:bg-icoDarkMode-anthracite',
                    { [background]: !isRead },
                    {
                        'bg-icoGray-100': isRead,
                        'p-4': !wide,
                        'py-4 pl-12 pr-8': wide
                    }
                )}>
                {wide && !isRead ? (
                    <span
                        className={clsx(
                            backgroundDot,
                            'absolute left-5.5 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full'
                        )}
                    />
                ) : null}
                {notificationClickAvailable ? (
                    <Notification.GoToUrl
                        goToUrl={goToUrl}
                        goToExternalUrl={goToExternalUrl}
                        onClick={onClick}
                        dataNotification={dataNotification}
                    />
                ) : null}
                <span
                    className={clsx('flex flex-1 items-center transition-opacity duration-500', {
                        'opacity-0': contentHidden,
                        'opacity-100': !contentHidden,
                        'gap-x-3': !wide,
                        'gap-x-5': wide,
                        'pointer-events-none': !dataNotification?.data?.action
                    })}>
                    {React.Children.map(children, (child) => {
                        if (child) {
                            if (typeof child['type'] === 'string') {
                                return child;
                            }
                            const newChild = React.cloneElement(
                                child as ReactElement<
                                    NotificationIconProps | NotificationContentProps
                                >,
                                {
                                    wide,
                                    type
                                }
                            );
                            return newChild;
                        }
                        return null;
                    })}
                </span>
                {wide && notificationClickAvailable ? (
                    <span className="flex items-center space-x-2 text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                        <span className="hidden opacity-0 transition-all group-hover:opacity-100 md:block">
                            {t('commons:notifications.see_in_details')}
                        </span>
                        <Icon icon="chevron-right" />
                    </span>
                ) : null}
            </span>
        </div>
    );
}

const iconVariants: VariantsProps = {
    [PRIMARY]: 'bg-icoBlue dark:bg-icoBlue/20',
    [GREEN]: 'bg-icoGreen dark:bg-icoGreen/20',
    [ERROR_SECONDARY]: 'bg-icoRed dark:bg-icoRed/20',
    gray_100: 'bg-icoGray-100'
};

type NotificationIconProps = Pick<NotificationProps, Required<'type'>> & {
    icon: string;
};

function NotificationIcon({ type, icon }: NotificationIconProps) {
    const background = iconVariants[type];
    return (
        <span
            className={clsx(
                'flex h-[46px] w-[46px] items-center justify-center rounded-full',
                'border-2 border-white text-white dark:border-icoDarkMode-deepBlack',
                background
            )}>
            <Icon icon={icon} />
        </span>
    );
}

Notification.Icon = NotificationIcon;

const AvatarBackgroundVariants: VariantsProps = {
    [PRIMARY]: 'bg-icoBlue-100 dark:bg-icoBlue/20 text-icoBlue',
    [GREEN]: 'bg-icoGreen-100 dark:bg-icoGreen/20 text-icoGreen',
    [ERROR_SECONDARY]: 'bg-icoRed-100 dark:bg-icoRed/20 text-icoRed',
    gray_100:
        'bg-icoGray-300 dark:bg-icoDarkMode-deepBlack text-icoGray-500 dark:text-icoDarkMode-wolf'
};

type NotificationAvatarProps = Pick<NotificationProps, Required<'type'>> & {
    size?: SizesType;
    socialProfile: {
        external_api_data?: {
            avatar?: string;
        };
        slug?: string;
        social_network_type?: SocialProfileNetworkType;
    };
};

function NotificationAvatar({
    type = PRIMARY,
    size = 'base',
    socialProfile
}: NotificationAvatarProps) {
    const background = AvatarBackgroundVariants[type];

    if (!socialProfile) return null;

    return (
        <Avatar
            size={size}
            url={socialProfile?.external_api_data?.avatar ?? null}
            fallback={socialProfile?.slug?.charAt(0) ?? 'U'}
            profileType={socialProfile.social_network_type}
            customColorClass={background}
        />
    );
}

Notification.Avatar = NotificationAvatar;

type NotificationContentProps = Pick<
    NotificationProps,
    Required<'wide'> | Required<'type'> | Required<'children'>
>;

function NotificationContent({ wide, type, children }: NotificationContentProps) {
    return (
        <span className="flex-1 space-y-2 text-icoBlack dark:text-white">
            {React.Children.map(children, (child) => {
                if (child) {
                    if (typeof child['type'] === 'string') {
                        return child;
                    }

                    const newChild = React.cloneElement(
                        child as ReactElement<NotificationContentChildProps>,
                        {
                            wide,
                            type
                        }
                    );
                    return newChild;
                }
                return null;
            })}
        </span>
    );
}

Notification.Content = NotificationContent;

const textColorVariants: VariantsProps = {
    [PRIMARY]: 'text-icoBlue',
    [GREEN]: 'text-icoGreen',
    [ERROR_SECONDARY]: 'text-icoRed',
    gray_100: 'text-icoGray-500 dark:text-icoDarkMode-wolf'
};

type NotificationContentChildProps = Pick<
    NotificationProps,
    Required<'wide'> | Required<'type'> | Required<'children'>
> & {
    i18nKey?: string;
    values?: Record<string, unknown>;
    components?: ReactElement[] | Record<string, ReactElement>;
};

function NotificationTitle({
    wide,
    type,
    i18nKey,
    values = {},
    components = {},
    children
}: NotificationContentChildProps) {
    const textColor = textColorVariants[type];
    return (
        <span
            className={clsx('block text-sm', {
                'line-clamp-2': !wide
            })}>
            {i18nKey ? (
                <Trans
                    i18nKey={i18nKey}
                    values={values}
                    components={{
                        color: <span className={textColor} />,
                        bold: <span className="font-GBold" />,
                        capitalize: <span className="capitalize" />,
                        underline: <span className="underline" />,
                        ...components
                    }}
                />
            ) : null}
            {children}
        </span>
    );
}

Notification.Title = NotificationTitle;

function NotificationDescription({
    wide,
    type,
    i18nKey,
    values = {},
    components = {},
    children
}: NotificationContentChildProps) {
    const textColor = textColorVariants[type];
    return (
        <span
            className={clsx(
                'block font-GMedium text-xs text-icoGray-500 dark:text-icoDarkMode-wolf',
                {
                    'line-clamp-2': !wide
                }
            )}>
            {i18nKey ? (
                <Trans
                    i18nKey={i18nKey}
                    values={values}
                    components={{
                        color: <span className={clsx(textColor, 'font-GSemiBold')} />,
                        bold: <span className="font-GSemiBold" />,
                        capitalize: <span className="capitalize" />,
                        underline: <span className="underline" />,
                        ...components
                    }}
                />
            ) : null}
            {children}
        </span>
    );
}

Notification.Description = NotificationDescription;

type NotificationReduceButtonProps = {
    onClick: () => void;
};

function NotificationReduceButton({ onClick }: NotificationReduceButtonProps) {
    return (
        <button
            className="absolute right-2 top-2 flex h-2 w-m items-center justify-center overflow-hidden rounded-full bg-white text-icoGray-500 dark:bg-icoDarkMode-deepBlack dark:text-icoDarkMode-wolf"
            onClick={onClick}>
            <Icon icon="reduce" />
        </button>
    );
}

Notification.ReduceButton = NotificationReduceButton;

const progressBarVariants: VariantsProps = {
    [PRIMARY]: 'bg-icoBlue',
    [GREEN]: 'bg-icoGreen',
    [ERROR_SECONDARY]: 'bg-icoRed',
    gray_100: 'bg-icoGray-500 dark:bg-icoDarkMode-wolf'
};

type NotificationProgressBarProps = Pick<NotificationProps, Required<'type'>> & {
    duration?: number;
    pause?: boolean;
    onAnimationEnd?: () => void;
};

function NotificationProgressBar({
    type,
    pause = false,
    onAnimationEnd,
    duration = 5000
}: NotificationProgressBarProps) {
    const background = progressBarVariants[type];
    return (
        <span>
            <span
                className={clsx(
                    'animation-progress-bar-decrease absolute bottom-0 left-0 m-0 h-0.75 w-full origin-left',
                    background
                )}
                onAnimationEnd={onAnimationEnd}
                style={{
                    animationDuration: `${duration}ms`,
                    animationPlayState: pause ? 'paused' : 'running'
                }}
            />
        </span>
    );
}

Notification.ProgressBar = NotificationProgressBar;

type NotificationCancelButtonProps = Pick<NotificationProps, 'dataNotification'> & {
    onClick?: () => void;
};

function NotificationCancelButton({ dataNotification, onClick }: NotificationCancelButtonProps) {
    const type = dataNotification?.options?.variant ?? PRIMARY;
    const { t } = useTranslation('commons');
    return (
        <Button
            size="sm"
            className="pointer-events-auto min-w-[5rem]"
            variant={type === 'gray_100' ? PRIMARY : type}
            onClick={onClick}>
            {t('cancel')}
        </Button>
    );
}

Notification.CancelButton = NotificationCancelButton;

type NotificationGoToUrlProps = Pick<
    NotificationProps,
    'goToUrl' | 'goToExternalUrl' | 'onClick' | 'dataNotification'
>;

function NotificationGoToUrl({
    goToUrl,
    goToExternalUrl,
    onClick,
    dataNotification
}: NotificationGoToUrlProps) {
    const router = useRouter();
    const { markAsRead } = useMarkAsReadNotification();

    useEffect(() => {
        if (typeof goToUrl === 'string') {
            router.prefetch(goToUrl);
        }
    }, [goToUrl]);

    const handleClick = () => {
        if (dataNotification.category !== 'custom' && !dataNotification?.read) {
            markAsRead(dataNotification.id);
        }

        // Hide toast to remove it from context
        if (typeof dataNotification?.options?.hideToast === 'function') {
            dataNotification.options.hideToast();
        }

        // Action
        if (typeof goToUrl === 'string') {
            router.push(goToUrl);
        } else if (typeof goToExternalUrl === 'string') {
            window.open(goToExternalUrl, '_blank', 'noopener,noreferrer');
        } else if (typeof onClick === 'function') {
            onClick();
        }
    };

    return <button onClick={handleClick} className="absolute inset-0 h-full w-full focus:ring-0" />;
}

Notification.GoToUrl = NotificationGoToUrl;

export default Notification;
